import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fetchWithAuth } from "../../utils/api";
import fallbackImage from "../../assets/placeholder.png";
import ThemedDatePicker from "../../components/ThemedDatePicker";

const AdminEvents = () => {
  const theme = useTheme();

  const [eventSearch, setEventSearch] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [eventDraft, setEventDraft] = useState(null);
  const [originalEvent, setOriginalEvent] = useState(null);

  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);

  const [itemInput, setItemInput] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState("");

  const [snackbar, setSnackbar] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const isDirty =
    JSON.stringify(eventDraft) !== JSON.stringify(originalEvent) ||
    JSON.stringify(items.map(i => i.id)) !== JSON.stringify(originalItems.map(i => i.id));

  const lockDisabled = !!selectedEvent;

  const showError = (msg) => {
    setSnackbar(msg);
    setSnackbarOpen(true);
  };

  // ----------------------------
  // SEARCH EVENT
  // ----------------------------
  const handleSearchEvent = async () => {
    if (!eventSearch.trim() || lockDisabled) return;

    try {
      const res = await fetchWithAuth(
        `/admin/events/search?q=${encodeURIComponent(eventSearch.trim())}`
      );

      const full = await fetchWithAuth(`/admin/events/${res.id}`);
      const normalizedEvent = {
        ...full.event,
        start_date: full.event.start_date
          ? full.event.start_date.slice(0, 10)
          : "",
        end_date: full.event.end_date
          ? full.event.end_date.slice(0, 10)
          : "",
      };

      setSelectedEvent(normalizedEvent);
      setEventDraft(normalizedEvent);
      setOriginalEvent(normalizedEvent);

      setItems(full.items || []);
      setOriginalItems(full.items || []);

      setEventSearch("");
    } catch (err) {
      showError("Event not found.");
    }
  };

  // ----------------------------
  // CREATE EVENT
  // ----------------------------
  const handleCreateEvent = async () => {
    try {
      const res = await fetchWithAuth("/admin/events", {
        method: "POST",
        body: eventDraft,
      });

      const full = await fetchWithAuth(`/admin/events/${res.id}`);
      const normalizedEvent = {
        ...full.event,
        start_date: full.event.start_date
          ? full.event.start_date.slice(0, 10)
          : "",
        end_date: full.event.end_date
          ? full.event.end_date.slice(0, 10)
          : "",
      };

      setSelectedEvent(normalizedEvent);
      setEventDraft(normalizedEvent);
      setOriginalEvent(normalizedEvent);

      setItems(full.items || []);
      setOriginalItems(full.items || []);

      setCreateOpen(false);
    } catch (err) {
      showError("Failed to create event.");
    }
  };

  // ----------------------------
  // UPDATE EVENT + ITEMS
  // ----------------------------
  const handleSave = async () => {
    if (!selectedEvent) return;

    try {
      // event update
      if (JSON.stringify(eventDraft) !== JSON.stringify(originalEvent)) {
        await fetchWithAuth(`/admin/events/${selectedEvent.id}`, {
          method: "PUT",
          body: eventDraft,
        });
      }

      // items update
      if (
        JSON.stringify(items.map(i => i.id)) !==
        JSON.stringify(originalItems.map(i => i.id))
      ) {
        await fetchWithAuth(`/admin/events/${selectedEvent.id}/items`, {
          method: "PUT",
          body: {
            item_ids: items.map((i) => i.id),
          },
        });
      }

      setOriginalEvent(eventDraft);
      setOriginalItems(items);

      setSnackbar("Saved successfully.");
      setSnackbarOpen(true);
    } catch (err) {
      showError("Save failed.");
    }
  };

  // ----------------------------
  // DELETE EVENT
  // ----------------------------
  const handleDelete = async () => {
    if (!selectedEvent) return;
    if (deleteText !== selectedEvent.name) {
      showError("Event name does not match.");
      return;
    }

    try {
      await fetchWithAuth(`/admin/events/${selectedEvent.id}`, {
        method: "DELETE",
      });

      setSelectedEvent(null);
      setEventDraft(null);
      setOriginalEvent(null);
      setItems([]);
      setOriginalItems([]);

      setDeleteConfirm(false);
      setDeleteText("");

      setSnackbar("Event deleted.");
      setSnackbarOpen(true);
    } catch (err) {
      showError("Delete failed.");
    }
  };

  // ----------------------------
  // CLEAR EVENT
  // ----------------------------
  const handleClear = () => {
    setSelectedEvent(null);
    setEventDraft(null);
    setOriginalEvent(null);
    setItems([]);
    setOriginalItems([]);
  };

  // ----------------------------
  // ITEM SEARCH
  // ----------------------------
  const handleItemSearch = async () => {
    if (!itemInput.trim() || !selectedEvent) return;

    const names = itemInput
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);

    try {
      const res = await fetchWithAuth(
        "/admin/items/search-bulk-for-events",
        {
          method: "POST",
          body: { names },
        }
      );

      const itemsRaw =
        res.items ||
        res.results ||
        res.data ||
        (Array.isArray(res) ? res : []);

      if (!Array.isArray(itemsRaw)) {
        throw new Error("Invalid response shape from backend");
      }

      const mapped = itemsRaw.map((i) => ({
        id: i.id,
        name: i.name,
        image: i.image_path || i.imageUrl || fallbackImage,
      }));

      const existing = new Set(items.map((i) => i.id));
      setItems((prev) => [
        ...prev,
        ...mapped.filter((i) => !existing.has(i.id)),
      ]);

      console.log(items, "items")
      setItemInput("");

      if (res.rejected?.length) {
        showError(`Not found: ${res.rejected.join(", ")}`);
      }
    } catch (err) {
      console.error("ITEM SEARCH ERROR:", err);
      showError("Item search failed.");
    }
  };

  // ----------------------------
  // REMOVE ITEM
  // ----------------------------
  const removeItem = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  // ----------------------------
  // RENDER
  // ----------------------------
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
          Admin Events
        </Typography>

        {/* SEARCH + CREATE */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="Search Event"
              value={eventSearch}
              disabled={lockDisabled}
              onChange={(e) => setEventSearch(e.target.value)}
            />

            <Button
              variant="contained"
              disabled={lockDisabled}
              onClick={handleSearchEvent}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              disabled={lockDisabled}
              onClick={() => {
                setCreateOpen(true);
                setEventDraft({
                  name: "",
                  description: "",
                  start_date: "",
                  end_date: "",
                });
              }}
            >
              Create
            </Button>

            {selectedEvent && (
              <Button color="error" onClick={handleClear}>
                Clear
              </Button>
            )}
          </Box>
        </Paper>

        {/* EVENT DETAILS */}
        {selectedEvent && eventDraft && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Event Details</Typography>

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Name"
              value={eventDraft.name || ""}
              onChange={(e) =>
                setEventDraft({ ...eventDraft, name: e.target.value })
              }
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Description"
              value={eventDraft.description || ""}
              onChange={(e) =>
                setEventDraft({ ...eventDraft, description: e.target.value })
              }
            />

            <ThemedDatePicker
              label="Start Date"
              value={eventDraft.start_date}
              onChange={(val) =>
                setEventDraft((prev) => ({
                  ...prev,
                  start_date: val,
                }))
              }
              theme={theme}
              restrictFutureDates={true}
            />

            <ThemedDatePicker
              label="End Date"
              value={eventDraft.end_date}
              onChange={(val) =>
                setEventDraft((prev) => ({
                  ...prev,
                  end_date: val,
                }))
              }
              theme={theme}
              restrictFutureDates={false}
            />

          </Paper>
        )}

        {/* ITEMS */}
        {selectedEvent && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Event Items</Typography>

            <Box display="flex" gap={2} mt={2}>
              <TextField
                fullWidth
                label="Comma-separated item names"
                value={itemInput}
                onChange={(e) => setItemInput(e.target.value)}
              />

              <Button variant="contained" onClick={handleItemSearch}>
                Add
              </Button>
            </Box>

            <Box
              mt={2}
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(140px, 1fr))"
              gap={2}
            >
              {items.map((item) => (
                <Paper
                  key={item.id}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    position: "relative",
                    background: theme.palette.background.secondPaper,
                    boxShadow: "none",
                    borderStyle: "solid",
                    borderColor: theme.palette.background.guideCard,
                    borderWidth: "1px",
                    borderRadius: "5px"
                  }}
                >
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeItem(item.id)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      padding: 0.5,
                      minWidth: "25px !important"
                    }}
                  >
                    X
                  </Button>

                  <img
                    src={item.image || item.imageUrl || fallbackImage}
                    alt={item.name}
                    style={{ width: "100%", height: 80, objectFit: "contain" }}
                  />

                  <Typography variant="body2">{item.name}</Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        )}

        {/* SAVE + DELETE */}
        {selectedEvent && (
          <Box display="flex" gap={2} mt={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty}
            >
              Save Changes
            </Button>

            <Button
              fullWidth
              color="error"
              variant="outlined"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete Event
            </Button>
          </Box>
        )}

        {/* CREATE MODAL */}
        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
          <DialogTitle>Create Event</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Name"
              value={eventDraft?.name || ""}
              onChange={(e) =>
                setEventDraft({ ...eventDraft, name: e.target.value })
              }
            />

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Description"
              value={eventDraft?.description || ""}
              onChange={(e) =>
                setEventDraft({ ...eventDraft, description: e.target.value })
              }
            />

            <ThemedDatePicker
              label="Start Date"
              value={eventDraft?.start_date}
              onChange={(val) =>
                setEventDraft((prev) => ({
                  ...prev,
                  start_date: val,
                }))
              }
              theme={theme}
              restrictFutureDates={true}
            />

            <ThemedDatePicker
              label="End Date"
              value={eventDraft?.end_date}
              onChange={(val) =>
                setEventDraft((prev) => ({
                  ...prev,
                  end_date: val,
                }))
              }
              theme={theme}
              restrictFutureDates={false}
            />

          </DialogContent>

          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateEvent}>
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* DELETE CONFIRM */}
        <Dialog
          open={deleteConfirm}
          onClose={() => setDeleteConfirm(false)}
          fullWidth
        >
          <DialogTitle>Delete Event</DialogTitle>
          <DialogContent>
            <Typography>
              Type <b>{selectedEvent?.name}</b> to confirm deletion.
            </Typography>

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setDeleteConfirm(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbar}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AdminEvents;